import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router";
import { Button, Input, FormField, Alert, Spinner, Heading } from "@/components/ds";
import styles from "./ResetPasswordForm.module.css";

interface ResetPasswordFormProps {
  readonly token: string;
}

type TokenState = "validating" | "valid" | "invalid";

export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const navigate = useNavigate();
  const [tokenState, setTokenState] = useState<TokenState>("validating");
  const [tokenError, setTokenError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function validate() {
      try {
        const response = await fetch("/api/auth/validate-reset-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          setTokenError(data.error?.message ?? "This reset link is not valid.");
          setTokenState("invalid");
          return;
        }

        setTokenState("valid");
      } catch {
        setTokenError("Something went wrong. Please try again.");
        setTokenState("invalid");
      }
    }

    validate();
  }, [token]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      navigate(data.data.redirectTo);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (tokenState === "validating") {
    return (
      <div className={styles.center}>
        <Spinner />
      </div>
    );
  }

  if (tokenState === "invalid") {
    return (
      <div className={styles.errorContainer}>
        <Heading level={1} size="h3">Reset password</Heading>
        <Alert variant="danger">{tokenError}</Alert>
        <p className={styles.footer}>
          <Link to="/forgot-password" className={styles.link}>Request a new one</Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <Heading level={1} size="h3">Reset your password</Heading>

      {error && <Alert variant="danger">{error}</Alert>}

      <FormField label="New password" htmlFor="password" hint="At least 8 characters">
        <Input
          type="password"
          name="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
        />
      </FormField>

      <FormField label="Confirm password" htmlFor="confirmPassword">
        <Input
          type="password"
          name="confirmPassword"
          id="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={loading}
        />
      </FormField>

      <Button type="submit" disabled={loading} fullWidth>
        {loading ? <Spinner size="sm" /> : "Reset password"}
      </Button>
    </form>
  );
}
